import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Calendar } from '../components/ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { 
  CalendarIcon, 
  Clock, 
  CheckCircle, 
  Phone, 
  Mail, 
  MapPin,
  Home,
  User,
  Loader2,
  ArrowRight
} from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '../lib/utils';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const RequestHomeVisitPage = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [date, setDate] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    postcode: '',
    preferredTime: '',
    notes: ''
  });

  const timeSlots = [
    { value: 'morning', label: 'Morning (9am - 12pm)' },
    { value: 'afternoon', label: 'Afternoon (12pm - 5pm)' },
    { value: 'evening', label: 'Evening (5pm - 8pm)' },
    { value: 'flexible', label: 'Flexible - Any time' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const isFormValid = () => {
    return formData.name && 
           formData.email && 
           formData.phone && 
           formData.address && 
           formData.postcode && 
           date && 
           formData.preferredTime;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isFormValid()) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/api/calculator/request-home-visit-from-estimate`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        address: formData.address,
        postcode: formData.postcode,
        preferredDate: format(date, 'yyyy-MM-dd'),
        preferredTime: formData.preferredTime,
        notes: formData.notes,
        source: 'estimate_email'
      });

      if (response.data) {
        setIsSuccess(true);
        toast({
          title: "Request Submitted!",
          description: "We'll contact you shortly to confirm your appointment."
        });
      }
    } catch (error) {
      console.error('Error submitting home visit request:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to submit request. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#f8faf9] to-white flex items-center justify-center p-4">
        <Card className="max-w-lg w-full shadow-xl border-0">
          <CardContent className="p-8 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-10 w-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Home Visit Request Received!
            </h2>
            <p className="text-gray-600 mb-6">
              Thank you for requesting a home visit, <strong>{formData.name}</strong>. 
              We've sent a confirmation email to <strong>{formData.email}</strong>.
            </p>
            <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
              <h3 className="font-semibold text-gray-900 mb-2">Your Requested Details:</h3>
              <div className="space-y-2 text-sm text-gray-600">
                <p><CalendarIcon className="inline h-4 w-4 mr-2" />{format(date, 'EEEE, do MMMM yyyy')}</p>
                <p><Clock className="inline h-4 w-4 mr-2" />{timeSlots.find(t => t.value === formData.preferredTime)?.label}</p>
                <p><MapPin className="inline h-4 w-4 mr-2" />{formData.address}, {formData.postcode}</p>
              </div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800">
              <strong>What happens next?</strong>
              <p className="mt-1">We will contact you within 24 hours to confirm your appointment date and time.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f8faf9] to-white">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center gap-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_window-quotes-1/artifacts/kzwoymfj_Aston%20Lily%20Shutters%20Web%20Banner%20.png" 
              alt="Aston Lily Shutters" 
              className="h-12"
            />
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Hero Section */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#8CB89F]/10 text-[#8CB89F] mb-4">
            <Home className="h-5 w-5" />
            <span className="font-medium">Free Home Visit</span>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Book Your Free Home Survey
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            You've seen our online estimate - now let us visit your home to provide an accurate, 
            no-obligation quote tailored to your exact requirements.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Form */}
          <div className="lg:col-span-2">
            <Card className="shadow-lg border-0">
              <CardHeader>
                <CardTitle>Your Details</CardTitle>
                <CardDescription>
                  Fill in your information and preferred appointment time
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Personal Details */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="flex items-center gap-2">
                        <User className="h-4 w-4 text-gray-400" />
                        Full Name *
                      </Label>
                      <Input
                        id="name"
                        placeholder="John Smith"
                        value={formData.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        data-testid="name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email" className="flex items-center gap-2">
                        <Mail className="h-4 w-4 text-gray-400" />
                        Email Address *
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="john@example.com"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        data-testid="email-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone" className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-gray-400" />
                      Phone Number *
                    </Label>
                    <Input
                      id="phone"
                      placeholder="07123 456789"
                      value={formData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      data-testid="phone-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="address" className="flex items-center gap-2">
                      <Home className="h-4 w-4 text-gray-400" />
                      Full Address *
                    </Label>
                    <Input
                      id="address"
                      placeholder="123 High Street, Bristol"
                      value={formData.address}
                      onChange={(e) => handleInputChange('address', e.target.value)}
                      data-testid="address-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="postcode" className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-gray-400" />
                      Postcode *
                    </Label>
                    <Input
                      id="postcode"
                      placeholder="BS1 1AA"
                      value={formData.postcode}
                      onChange={(e) => handleInputChange('postcode', e.target.value.toUpperCase())}
                      className="uppercase"
                      data-testid="postcode-input"
                    />
                  </div>

                  {/* Date and Time Selection */}
                  <div className="border-t pt-6">
                    <h3 className="font-semibold text-gray-900 mb-4">Preferred Appointment</h3>
                    
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2">
                          <CalendarIcon className="h-4 w-4 text-gray-400" />
                          Preferred Date *
                        </Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              className={cn(
                                "w-full justify-start text-left font-normal",
                                !date && "text-muted-foreground"
                              )}
                              data-testid="date-picker"
                            >
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {date ? format(date, "PPP") : "Select a date"}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={date}
                              onSelect={setDate}
                              disabled={(date) => 
                                date < new Date() || 
                                date < new Date(new Date().setHours(0, 0, 0, 0))
                              }
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                      </div>

                      <div className="space-y-2">
                        <Label className="flex items-center gap-2">
                          <Clock className="h-4 w-4 text-gray-400" />
                          Preferred Time *
                        </Label>
                        <Select
                          value={formData.preferredTime}
                          onValueChange={(value) => handleInputChange('preferredTime', value)}
                        >
                          <SelectTrigger data-testid="time-select">
                            <SelectValue placeholder="Select a time slot" />
                          </SelectTrigger>
                          <SelectContent>
                            {timeSlots.map((slot) => (
                              <SelectItem key={slot.value} value={slot.value}>
                                {slot.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <p className="text-sm text-amber-600 bg-amber-50 p-3 rounded-lg mt-4">
                      <strong>Note:</strong> We will contact you to confirm your appointment date and time. 
                      The date and time you select is your preference - we'll do our best to accommodate it.
                    </p>
                  </div>

                  {/* Additional Notes */}
                  <div className="space-y-2">
                    <Label htmlFor="notes">Additional Notes (Optional)</Label>
                    <Textarea
                      id="notes"
                      placeholder="Any specific requirements or information we should know?"
                      value={formData.notes}
                      onChange={(e) => handleInputChange('notes', e.target.value)}
                      rows={3}
                      data-testid="notes-input"
                    />
                  </div>

                  {/* Submit Button */}
                  <Button 
                    type="submit" 
                    className="w-full bg-[#8CB89F] hover:bg-[#7aa88f] text-white py-6 text-lg"
                    disabled={isSubmitting || !isFormValid()}
                    data-testid="submit-btn"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        Request Home Visit
                        <ArrowRight className="ml-2 h-5 w-5" />
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* What to Expect */}
            <Card className="border-0 shadow-lg bg-[#8CB89F]/5">
              <CardHeader>
                <CardTitle className="text-lg">What to Expect</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#8CB89F] text-white flex items-center justify-center text-sm font-bold">1</div>
                  <div>
                    <p className="font-medium text-gray-900">Confirmation Call</p>
                    <p className="text-sm text-gray-600">We'll call to confirm your appointment</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#8CB89F] text-white flex items-center justify-center text-sm font-bold">2</div>
                  <div>
                    <p className="font-medium text-gray-900">Home Survey</p>
                    <p className="text-sm text-gray-600">Our expert will measure your windows</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#8CB89F] text-white flex items-center justify-center text-sm font-bold">3</div>
                  <div>
                    <p className="font-medium text-gray-900">Detailed Quote</p>
                    <p className="text-sm text-gray-600">Receive your accurate, no-obligation quote</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Trust Badges */}
            <Card className="border-0 shadow-lg">
              <CardContent className="p-6 space-y-4">
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  <span className="text-sm text-gray-700">10 Year Structural Warranty</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  <span className="text-sm text-gray-700">Family Owned Business</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  <span className="text-sm text-gray-700">No Obligation Quote</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  <span className="text-sm text-gray-700">Professional Installation</span>
                </div>
              </CardContent>
            </Card>

            {/* Contact */}
            <Card className="border-0 shadow-lg bg-gray-900 text-white">
              <CardContent className="p-6">
                <p className="font-medium mb-4">Prefer to call us?</p>
                <a 
                  href="tel:01onal275314990" 
                  className="flex items-center gap-2 text-xl font-bold hover:text-[#8CB89F] transition-colors"
                >
                  <Phone className="h-5 w-5" />
                  01275 314990
                </a>
                <p className="text-sm text-gray-400 mt-2">Mon-Sun: 9am - 8pm</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RequestHomeVisitPage;
